import { Injectable } from '@angular/core';
import {Observable} from "rxjs";
import {map, mergeMap} from "rxjs/operators";
import {plainToInstance} from "class-transformer";
import {TranslocoService} from "@jsverse/transloco";
import { MesApiService } from '../../mes-api.service';
import { LocalizationModel } from './localization.model';
import {LanguagesQuery} from "./language.store";
import {ApiResponse} from "../../api-response/api-response.model";
import {ToastrService} from "ngx-toastr";

@Injectable({
  providedIn: 'root'
})
export class LocalizationService extends MesApiService {
  constructor(private languagesQuery: LanguagesQuery, private translocoService: TranslocoService, private toastrService: ToastrService) {
    super('config');
  }

  save(localizations: LocalizationModel[]): Observable<LocalizationModel[]> {
    return this.http.put<ApiResponse<LocalizationModel[]>>(this.getRoute({action: 'Put'}), localizations).pipe(map(value => {
      return plainToInstance(LocalizationModel, value.data);
    }));
  }

  addKeys(keys: string[]): Observable<ApiResponse<LocalizationModel[]>> {
    const availableLangs = this.translocoService.getAvailableLangs() as string[];
    return this.languagesQuery.getAll$.pipe(mergeMap(languages => {
      let localizationModels: LocalizationModel[] = [];
      const availableLanguages = languages.filter(language => availableLangs.includes(language.Iso2));

      const helpKeys = keys.filter(key => key.startsWith('help'));
      keys = keys.filter(key => !key.startsWith('help'));

      helpKeys.map(key => {
        localizationModels.push(new LocalizationModel({Key: key}));
      });

      availableLanguages.forEach(language => {
        keys.forEach(key => localizationModels.push(new LocalizationModel({Key: key, LanguageId: language.Id})));
      });
      return this.http.post(this.getRoute({action: 'AddKeys'}), localizationModels);
    })).pipe(map(value => value as ApiResponse<LocalizationModel[]>));
  }

  addKeysFromPath(path: string) {
    return this.http.get(`${path}${this.translocoService.getDefaultLang()}.json`)
      .pipe(mergeMap(data => {
        return this.addKeys(Object.keys(data));
      }))
      .pipe(map(response => {
        const message = response.data.length === 0
          ? 'No new keys found.'
          : `${response.data.length} new key(s) added to the database.`;
        this.toastrService.success(message);
        return response;
      }));
  }

  getLocalizationsByLanguage(culture: string): Observable<LocalizationModel[]> {
    return this.getList<LocalizationModel>({action: 'GetLocalizationsByLanguage', query: {culture: culture}}).pipe(map(value => {
      return plainToInstance(LocalizationModel, value.data);
    }));
  }

  getExtractedLocalizationsByLanguage(keys: string[], languageId: string): Observable<LocalizationModel[]> {
    let keysWithLanguage: LocalizationModel[] = [];
    keys.forEach(key => keysWithLanguage.push(new LocalizationModel({Key: key, LanguageId: languageId})));

    return this.http.post<ApiResponse<LocalizationModel[]>>(this.getRoute({action: 'GetExtractedLocalizationsByLanguage'}), keysWithLanguage).pipe(map(value => {
      return plainToInstance(LocalizationModel, value.data);
    }));
  }

  getLocalizationInAllLanguages(key: string): Observable<LocalizationModel[]> {
    return this.http.get<ApiResponse<LocalizationModel[]>>(`${this.getRoute({action: 'GetLocalizationInAllLanguages'})}?key=${key}`).pipe(map(value => {
      return plainToInstance(LocalizationModel, value.data);
    }));
  }

  edit(localizationModel: LocalizationModel) {
    return this.http.put<ApiResponse<LocalizationModel>>(this.getRoute({action: 'Edit'}), localizationModel).pipe(map(value => {
      return plainToInstance(LocalizationModel, value.data);
    }));
  }

  remove(localizationModel: LocalizationModel) {
    return this.delete<LocalizationModel>(localizationModel.Id).pipe(map(value => {
      return plainToInstance(LocalizationModel, value.data);
    }));
  }

  reset() {
    return this.getSingle<boolean>({action: 'ResetAll'});
  }
}
